<template>
  <label v-if="!labelDisabled"
         class="form-label"
         :class="[labelClass, { required }]"
         :for="parsedName"
  >
    <span v-if="required">*</span>{{ parsedLabel }}
  </label>

  <div class="input-group"
       :class="[
           inputGroupClass,
           {
             'dragover': dragover,
             'is-invalid': error,
             'input-group--focus': isFocus,
             'input-group--sm': small,
             'input-group--disabled': isDisabled,
             'input-group--field': field,
             'input-group--select': type === 'select',
             'input-group--datepicker': type === 'datepicker'
           },
       ]"
  >
    <div v-if="field"
         class="input-group-text"
    >
      <FormCheckbox
          v-model="enabled"
          :name="field"
          :disabled="disabled && !enableField"
          :hide-input="hideFieldInput"
          label-disabled
      />
    </div>

    <div v-if="hasPrefix"
         class="input-group-text"
    >
      <slot name="prefix" />
    </div>

    <div v-if="loading"
         class="input-group-text"
    >
      <Spinner />
    </div>

    <div v-if="isColorPicker"
         class="input-group-text"
    >
      <ColorPicker
          ref="colorPickerRef"
          v-model="inputValue"
          :disabled="isDisabled"
          class="circle-20 border"
      />
    </div>

    <!--- INPUT --->
    <component
        ref="inputRef"
        :is="inputComponent"
        v-bind="{...props, ...$attrs}"
        v-model="inputValue"
        v-model:range-start="props.rangeStart"
        v-model:range-end="props.rangeEnd"
        :disabled="isDisabled"
        :show-password="showPassword"
        :class="{'pe-0': hasSuffix || showInteractiveButtons || isPasswordType || showClear}"
        @focus="onFocus"
        @blur="onBlur"
        @click="onClick"
        @change="onChange"
        @select="onSelect"
        @dragover="dragover = true"
        @dragleave="dragover = false"
        @open="emit('open')"
        @close="emit('close')"
        @enter="emit('enter')"
    >
      <!-- select slots -->
      <template #input="data">
        <slot name="input" v-bind="data" />
      </template>
      <template #template="data">
        <slot name="template" v-bind="data" />
      </template>
      <template #emptyList="data">
        <slot name="emptyList" v-bind="data" />
      </template>
      <template #option="data">
        <slot name="option" v-bind="data" />
      </template>
      <template #output="data">
        <slot name="output" v-bind="data" />
      </template>
      <template #search="data">
        <slot name="search" v-bind="data" />
      </template>
    </component>

<!--    <input v-if="field && !enabled" type="hidden" :name="parsedName" value="" />-->

    <div v-if="showClear"
         class="input-group-text"
         @click="clear"
    >
      <i class="icon-times cursor-pointer" />
    </div>

    <div v-if="showInteractiveButtons"
         class="input-group-text interactive"
    >
      <i class="icon-check cursor-pointer" @click="saveNewValue" />
      <i class="icon-times cursor-pointer" @click="resetValue" />
    </div>

    <div v-if="isPasswordType"
         class="input-group-text cursor-pointer"
         @click="showPassword = !showPassword"
    >
      <i :class="{'icon-eye-lid': !showPassword, 'icon-eye text-primary':showPassword}" />
    </div>

    <!--- /INPUT --->

    <slot />

    <div v-if="hasSuffix"
         class="input-group-text"
    >
      <slot name="suffix" />
    </div>
  </div>

  <div v-if="infoText"
       class="form-text"
       v-html="infoText"
  />

  <div v-if="error && !disableErrorMessage"
       class="invalid-feedback"
       v-html="error"
  />
</template>
<script setup>
import formControlProps
  from "@/js/Elements/Form/FormControlProps.js";
import { useFormControl, useFormControlParser } from "@/js/Elements/Form/FormControl.js";
import {computed, onBeforeUnmount, ref, useSlots, watch} from "vue";
import Datepicker from "@/js/Elements/Datepicker.vue";
import Select from "@/js/Elements/Select/Select.vue";
import FormCheckbox from "@/js/Elements/Form/FormCheckbox.vue";
import FormAddressInput from "@/js/Elements/Form/FormAddressInput.vue";
import FormInput from "@/js/Elements/Form/FormInput.vue";
import ColorPicker from "@/js/Elements/ColorPicker.vue";
import axios from "axios";
import Spinner from "@/js/Elements/Spinner.vue";
import FormTimerPicker from "@/js/Elements/Form/FormTimerPicker.vue";
import FormInputWithSuggestions from "@/js/Elements/Form/FormInputWithSuggestions.vue";

const props = defineProps({
  ...formControlProps,
});
const {
  parsedLabel,
  parsedName
} = useFormControlParser(props);
defineOptions({
  inheritAttrs: false
});
const emit = defineEmits([
  'update:modelValue', 'update:rangeStart', 'update:rangeEnd',
  'focus', 'blur', 'changed', 'change', 'click', 'enter', 'select', 'save', 'open', 'close', 'unmount', 'clear', 'enter'
]);
const {
  inputValue,
  error
} = useFormControl(props, emit);
defineExpose({
  focus() {
    inputRef.value.focus();
  },
  clear,
  reload() {
    inputRef.value.reload();
  },
  resetValue,
  updateValue
});
const slots = useSlots();
const hasPrefix = computed(() => !!slots?.prefix);
const hasSuffix = computed(() => !!slots?.suffix);
const isFocus = ref(false);
const inputRef = ref();
const enabled = ref(props.field ? 0 : 1);
const isDisabled = computed(() => props.disabled || !enabled.value);
const isPasswordType = computed(() => props.type === 'password');
const showPassword = ref(false);
const colorPickerRef = ref();
const originalValue = ref(inputValue.value);
const loading = ref(false);
const dragover = ref(false);
const inputComponent = computed(() => {
  switch (props.type) {
    case 'address':
      return FormAddressInput;
    case 'select':
      return Select;
    case 'datepicker':
      return Datepicker;
    case 'timepicker':
      return FormTimerPicker;
    case 'input-with-suggestions':
      return FormInputWithSuggestions;
  }
  return FormInput;
});

if (inputComponent.value === FormInput  && typeof props.payload === 'string') {
  loading.value = true;
  axios.get(props.payload).then((response) => {
    inputValue.value = response.data;
  }).finally(() => loading.value = false);
}

const showInteractiveButtons = computed(() => {
  return props.interactive && inputValue.value !== originalValue.value && !loading.value;
});
const showClear = computed(() => {
  return ['select', 'datepicker', 'timepicker'].indexOf(props.type) === -1 && inputValue.value && props.clearable && !isDisabled.value;
});
const isColorPicker = computed(() => props.type === 'colorpicker');

if (props.type === 'datepicker' && props.range) {
  watch(() => [props.rangeStart, props.rangeEnd], (value) => {
    inputValue.value = value;
    emit('update:rangeStart', value[0]);
    emit('update:rangeEnd', value[1]);
  }, { deep: true });
}

onBeforeUnmount(() => {
  if (props.interactive) {
    emit('unmount', originalValue.value);
  }
});

function onKeyUp(e) {
  if (e.keyCode === 13) {
    emit('enter', e);
  }
}

function onFocus(e) {
  isFocus.value = true;

  if (isColorPicker.value) {
    colorPickerRef.value.open();
  }
  emit('focus', e);
}

function onClick(e) {
  emit('click', e);
}

function onBlur(e) {
  isFocus.value = false;
  emit('blur', e);
}

function onChange(value) {
  // todo: need more information about this bug
  // somehow it calls with argument "event"
  if (value?.type === 'change' && typeof value?.timeStamp !== 'undefined') return;
  inputValue.value = value;
  emit('change', inputValue.value);
}

function clear() {
  if (inputRef.value && typeof inputRef.value.clear === 'function') {
    inputRef.value.clear();
  }
  inputValue.value = null;
  emit('clear');
}

function onSelect(value) {
  emit('select', value);
}

function saveNewValue() {
  loading.value = true;
  emit('save', {
    new: inputValue.value,
    old: originalValue.value
  });
}

function updateValue() {
  loading.value = false;
  originalValue.value = inputValue.value;
}

function resetValue() {
  loading.value = false;
  inputValue.value = originalValue.value;
}
</script>

<style lang="scss">
@import "@/css/functions";
@import "@/css/variables";

.input-group {
  .input-group-text [class^="icon-"],
  .input-group-text[class^="icon-"] {
    color: themeColor('neutral-700');
  }

  &-lg .form-control {
    height: $input-lg-height;
  }

  &--select {
    flex-wrap: nowrap;
  }

  &-sm {
    .select {
      .v-popper--select {
        height: auto;
      }

      .select__output {
        padding: toRem(4) toRem(2) toRem(4) toRem(6);
        font-size: fontSize(5);
      }

      .select__arrow {
        padding: toRem(4);
      }
    }
  }
}

.form-label {
  font-size: $input-label-font-size;
  font-weight: $input-label-font-weight;
  min-height: toRem(18);

  > span {
    color: themeColor('primary');
    margin-right: spacing(1);
  }
}

.form-control {
  @if $input-icon-disabled {
    &.is-invalid {
      background-image: none !important;
    }
  }
  font-weight: $input-font-weight;

  &--search {
    border-radius: 50%;
  }
}
</style>

<style scoped lang="scss">
@import "@/css/functions";
@import "@/css/variables";

$requiredSpacing: spacing(6);

.input-group {
  overflow: hidden;
  border: themeColor('border') solid toRem(1);
  border-radius: $input-border-radius;

  &:hover {
    border-color: themeColor('neutral-400');
  }

  &.dragover {
    border: themeColor('primary') dashed toRem(2);
  }

  &--disabled {
    &:hover {
      border-color: themeColor('border');
    }
    .input-group-text {
      background-color: $input-disabled-bg;
    }

    :deep(.datepicker__input .form-control) {
      background-color: $input-disabled-bg;
    }

    &:deep(.select) {
      background-color: $input-disabled-bg;
      opacity: 1;

      .input-group-text,
      .select__output,
      .select__arrow {
        background-color: $input-disabled-bg;
      }
    }
  }

  &--focus {
    border-color: themeColor('primary');

    &:hover {
      border-color: themeColor('primary');
    }

    .form-control {
      background-color: themeColor('base');
    }

    &.input-group--select {
      &:before { display: none; }
    }
  }

  &--field {
    flex-wrap: nowrap;

    &.input-group--required {
      &:before {
        top: toRem(5);
        left: toRem(45);
      }

      :deep(.datepicker__input .form-control) {
        padding-left: $requiredSpacing;
      }
    }
  }

  &--select {
    &.input-group--required {
      :deep(.select__output) {
        padding-left: $requiredSpacing;
      }
    }
  }

  &--required {
    &:before {
      content: '*';
      position: absolute;
      top: toRem(3);
      left: toRem(8);
      z-index: 9;
      color: themeColor('primary-600');
      font-size: fontSize(1);
    }

    &.input-group--datepicker {
      &:before {
        top: toRem(5);
      }
    }

    :deep(.datepicker__input .form-control) {
      padding-left: $requiredSpacing;
    }

    input {
      padding-left: $requiredSpacing;
    }
  }

  &.is-invalid {
    border-color: themeColor('negative-500');
  }

  &--pill {
    background-color: themeColor('neutral-100');
    border-color: themeColor('border');
    border-radius: toRem(20);
    overflow: hidden;

    > .input-group-text {
      background-color: themeColor('neutal-100');
    }

    &.input-group--focus {
      border-color: themeColor('primary');
      background-color: themeColor('base');

      .form-control {
        background-color: themeColor('base');
      }

      > .input-group-text {
        background-color: themeColor('base');
      }
    }

    > .form-control {
      padding-left: 0;
      background-color: themeColor('neutral-100');
    }

    > .form-control,
    > .input-group-text {
      border: none;

      &:focus {
        box-shadow: none;
      }
    }
  }

  .input-group-text,
  .form-control,
  &:deep(.form-control),
  &:deep(.input-group-text) {
    font-size: fontSize(5);
    border: none !important;

    &:focus {
      box-shadow: none !important;
    }
  }

  .input-group-text.interactive {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: spacing(2);

    .icon-check {
      color: themeColor('success-500');
    }

    .icon-times {
      font-size: toRem(18);
    }
  }

  :deep(input:-webkit-autofill),
  :deep(input:focus:-webkit-autofill){
    box-shadow: 0 0 0px 1000px themeColor('base') inset !important;
    -webkit-text-fill-color: themeColor('text') !important;
    transition: background-color 5000s ease-in-out 0s;
  }
}
</style>